import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { DnsRecordsTable } from './Domains';
import { IconArrowLeft, IconArrowRight, IconCheck, IconRefresh, IconSend } from '../components/icons';
import { LetterLaunch, Logo, OceanBackdrop } from '../components/ocean';
import { CodeTabs, CopyButton, Dive, Field, sendSnippets, StatusPill, useToast } from '../components/ui';
import { api, ApiError, ApiKey, canSetUpWorkspace, Domain, EmailDetail, Me, OnboardingStep, Webhook, WEBHOOK_EVENTS } from '../lib/api';
import { useSession } from '../lib/session';
import { startActivity, track, trackPage } from '../lib/activity';
import { t } from '../lib/i18n';

const STEPS: { id: OnboardingStep | 'finish'; depth: string; zone: string; title: string; desc: string }[] = [
  { id: 'profile', depth: '0 м', zone: 'Поверхность', title: 'Профиль', desc: 'Компания и цели' },
  { id: 'domain', depth: '50 м', zone: 'Шельф', title: 'Домен', desc: 'SPF, DKIM, DMARC' },
  { id: 'api_key', depth: '200 м', zone: 'Риф', title: 'API-ключ', desc: 'Доступ для кода' },
  { id: 'test_email', depth: '1 000 м', zone: 'Глубина', title: 'Первое письмо', desc: 'Живой путь в мс' },
  { id: 'webhook', depth: '4 000 м', zone: 'Бездна', title: 'Вебхуки', desc: 'События в ваш сервер' },
  { id: 'finish', depth: '11 000 м', zone: 'Впадина', title: 'Готово', desc: 'Вы в океане' },
];

export function Onboarding() {
  const { me, setMe } = useSession();
  const nav = useNavigate();
  const completed = me?.workspace.onboarding.completed ?? [];
  const firstOpen = STEPS.findIndex((s) => s.id !== 'finish' && !completed.includes(s.id as OnboardingStep));
  const [index, setIndex] = useState(firstOpen === -1 ? STEPS.length - 1 : firstOpen);
  const [domain, setDomain] = useState<Domain | null>(null);
  const [apiKey, setApiKey] = useState<ApiKey | null>(null);

  // The wizard is outside the main layout: it starts the click-stream itself (step views and completions only).
  const stepId = STEPS[index]?.id;
  useEffect(() => {
    if (!me || me.support) return;
    startActivity();
    trackPage('/onboarding');
  }, [me]);
  useEffect(() => {
    if (stepId) track(`onboarding.step_view:${stepId}`);
  }, [stepId]);

  useEffect(() => {
    api.get<Domain[]>('/domains').then((ds) => setDomain(ds.find((d) => d.status === 'verified') ?? ds[0] ?? null)).catch(() => undefined);
  }, []);

  if (!me) return null;
  // Viewers and developers cannot change workspace settings; the wizard would only answer 403.
  if (!canSetUpWorkspace(me)) return <Navigate to="/" replace />;
  const step = STEPS[index];
  const fill = (index / (STEPS.length - 1)) * 100;

  async function complete(id: OnboardingStep) {
    const updated = await api.post<Me>('/onboarding/steps', { step: id });
    track(`onboarding.step_done:${id}`);
    setMe(updated);
    setIndex((i) => Math.min(i + 1, STEPS.length - 1));
  }
  const skip = () => {
    track('onboarding.skip', { step: String(STEPS[index]?.id ?? '') });
    setIndex((i) => Math.min(i + 1, STEPS.length - 1));
  };
  const back = () => setIndex((i) => Math.max(i - 1, 0));

  return (
    <div className="ocean-screen">
      <OceanBackdrop letters={10} />
      <div className="wizard">
        <aside className="glass depth">
          <div className="row">
            <Logo size={36} />
            <div>
              <strong style={{ color: '#fff' }}>{t('Погружение')}</strong>
              <div className="small muted">{me.workspace.name}</div>
            </div>
          </div>
          <div className="depth-track">
            <div className="depth-fill" style={{ height: `calc(${fill}% - 12px)` }} />
            {STEPS.map((s, i) => {
              const done = s.id !== 'finish' && completed.includes(s.id as OnboardingStep);
              return (
                <button
                  key={s.id}
                  className={`depth-step${i === index ? ' current' : done ? ' done' : ''}`}
                  onClick={() => setIndex(i)}
                  disabled={i > index && !done && i !== firstOpen}
                >
                  <div className="z">{t(s.depth)} · {t(s.zone)}</div>
                  <div className="n">{t(s.title)}</div>
                  <div className="d">{t(s.desc)}</div>
                </button>
              );
            })}
          </div>
          <button className="btn btn-ghost btn-sm" style={{ color: '#8aa6c1', marginTop: 8 }} onClick={() => nav('/')}>
            {t('Пропустить и открыть бэкофис')}
          </button>
        </aside>

        <section className="glass wizard-card" key={step.id}>
          <div className="small" style={{ color: 'var(--spray)', fontFamily: 'var(--mono)', marginBottom: 10 }}>
            {step.id === 'finish' ? t('ФИНИШ') : t('ШАГ {n} ИЗ 5', { n: index + 1 })} · {t('ГЛУБИНА {d}', { d: t(step.depth) })}
          </div>
          {step.id === 'profile' && <ProfileStep me={me} onDone={async (m) => (setMe(m), complete('profile'))} />}
          {step.id === 'domain' && <DomainStep done={completed.includes('domain')} domain={domain} setDomain={setDomain} onDone={() => complete('domain')} onSkip={skip} onBack={back} />}
          {step.id === 'api_key' && <ApiKeyStep done={completed.includes('api_key')} me={me} domain={domain} apiKey={apiKey} setApiKey={setApiKey} onDone={() => complete('api_key')} onBack={back} />}
          {step.id === 'test_email' && <TestEmailStep done={completed.includes('test_email')} me={me} domain={domain} onDone={() => complete('test_email')} onSkip={skip} onBack={back} />}
          {step.id === 'webhook' && <WebhookStep done={completed.includes('webhook')} onDone={() => complete('webhook')} onSkip={skip} onBack={back} />}
          {step.id === 'finish' && <FinishStep me={me} domain={domain} />}
        </section>
      </div>
    </div>
  );
}

// ---------------- steps ----------------

function ProfileStep({ me, onDone }: { me: Me; onDone: (m: Me) => Promise<unknown> }) {
  const [name, setName] = useState(me.workspace.name);
  const [website, setWebsite] = useState(me.workspace.website ?? '');
  const [useCase, setUseCase] = useState(me.workspace.use_case ?? 'transactional');
  const [volume, setVolume] = useState(me.workspace.monthly_volume ?? '<10k');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  async function save() {
    setBusy(true);
    setError('');
    try {
      const site = website && !/^https?:\/\//.test(website) ? `https://${website}` : website;
      const m = await api.patch<Me>('/workspace', { name, website: site, use_case: useCase, monthly_volume: volume });
      await onDone(m);
      toast(t('Профиль сохранён'));
    } catch (e) {
      setError(e instanceof ApiError && e.code === 'VALIDATION_FAILED' ? t('Проверьте адрес сайта') : (e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h1>{t('Добро пожаловать на поверхность, {name} 👋', { name: me.user.name.split(' ')[0] })}</h1>
      <p className="lead">{t('Пара вопросов, чтобы настроить океан под ваш продукт. Это займёт меньше минуты.')}</p>
      <div className="stack" style={{ marginTop: 24, gap: 18 }}>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
          <Field label={t('Компания')}>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label={t('Сайт')} hint={t('Нужен, чтобы подсказать домен отправки')}>
            <input className="input" placeholder="coffee-north.com" value={website} onChange={(e) => setWebsite(e.target.value)} />
          </Field>
        </div>
        <Field label={t('Что будете отправлять?')}>
          <div className="choice-grid">
            {[
              { v: 'transactional', t: '⚡ Транзакционные', s: 'Чеки, коды, сброс пароля, уведомления' },
              { v: 'marketing', t: '🌊 Маркетинговые', s: 'Рассылки, акции, цепочки' },
              { v: 'both', t: '🐋 И то, и другое', s: 'Раздельные потоки в одном аккаунте' },
            ].map((c) => (
              <button key={c.v} type="button" className={`choice${useCase === c.v ? ' on' : ''}`} onClick={() => setUseCase(c.v as typeof useCase)}>
                <strong>{t(c.t)}</strong>
                <span>{t(c.s)}</span>
              </button>
            ))}
          </div>
        </Field>
        <Field label={t('Объём в месяц')}>
          <div className="chips">
            {[
              ['<10k', 'до 10 тыс.'],
              ['10k-100k', '10–100 тыс.'],
              ['100k-1m', '100 тыс. – 1 млн'],
              ['>1m', 'больше 1 млн'],
            ].map(([v, l]) => (
              <button key={v} type="button" className={`chip${volume === v ? ' on' : ''}`} onClick={() => setVolume(v)}>{t(l)}</button>
            ))}
          </div>
        </Field>
        {error && <div className="callout bad">{error}</div>}
      </div>
      <div className="wizard-actions">
        <span className="spacer" />
        <button className="btn btn-primary btn-lg" onClick={save} disabled={busy || !name.trim()}>
          {t('Нырнуть глубже')} <IconArrowRight size={16} />
        </button>
      </div>
    </>
  );
}

function DomainStep({
  done,
  domain,
  setDomain,
  onDone,
  onSkip,
  onBack,
}: {
  done: boolean;
  domain: Domain | null;
  setDomain: (d: Domain) => void;
  onDone: () => Promise<void>;
  onSkip: () => void;
  onBack: () => void;
}) {
  const { me } = useSession();
  const suggested = useMemo(() => {
    try {
      return me?.workspace.website ? new URL(me.workspace.website).hostname.replace(/^www\./, '') : '';
    } catch {
      return '';
    }
  }, [me]);
  const [name, setName] = useState(suggested);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function add() {
    setBusy(true);
    setError('');
    try {
      setDomain(await api.post<Domain>('/domains', { name }));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function verify() {
    if (!domain) return;
    setBusy(true);
    try {
      setDomain(await api.post<Domain>(`/domains/${domain.id}/verify`));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h1>{t('Шельф: ваш домен отправки')}</h1>
      <p className="lead">
        {t('Письма должны приходить от вашего имени и проходить проверки Gmail, Outlook и Яндекса. Добавьте три DNS-записи — мы сгенерировали для вас ключ DKIM.')}
      </p>
      <div className="stack" style={{ marginTop: 24 }}>
        {!domain ? (
          <>
            <Field label={t('Домен')} hint={t('Совет: используйте поддомен, например mail.вашсайт.com — так репутация рассылок не смешивается с основным доменом')}>
              <div className="row">
                <input className="input" placeholder="mail.coffee-north.com" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && name && add()} />
                <button className="btn btn-primary" onClick={add} disabled={busy || !name}>{t('Добавить')}</button>
              </div>
            </Field>
            {error && <div className="callout bad">{error}</div>}
          </>
        ) : (
          <>
            <div className="row">
              <h3 style={{ color: '#fff' }}>{domain.name}</h3>
              <span className={`pill ${domain.status}`}>{domain.status === 'verified' ? t('Подтверждён') : t('Ждём DNS')}</span>
              <span className="spacer" />
              <button className="btn" onClick={verify} disabled={busy}>
                <IconRefresh size={15} /> {busy ? t('Проверяем…') : t('Проверить DNS')}
              </button>
            </div>
            <DnsRecordsTable domain={domain} />
            {domain.status === 'verified' ? (
              <div className="callout ok"><IconCheck /> {t('Домен подтверждён. Письма будут подписаны DKIM и пройдут SPF.')}</div>
            ) : (
              <div className="callout warn">
                {t('Изменения DNS обычно применяются за 5–30 минут, иногда до 48 часов. Можно продолжить: первое тестовое письмо мы отправим с домена {domain}.', { domain: me?.platform.sender_domain ?? '' })}
              </div>
            )}
          </>
        )}
      </div>
      <div className="wizard-actions">
        <button className="btn btn-ghost" onClick={onBack}><IconArrowLeft size={16} /> {t('Назад')}</button>
        <span className="spacer" />
        <button className="btn btn-ghost" onClick={onSkip}>{t('Настрою позже')}</button>
        <button className="btn btn-primary btn-lg" disabled={domain?.status !== 'verified' && !done} onClick={onDone}>
          {t('Дальше')} <IconArrowRight size={16} />
        </button>
      </div>
    </>
  );
}

function ApiKeyStep({
  done,
  me,
  domain,
  apiKey,
  setApiKey,
  onDone,
  onBack,
}: {
  done: boolean;
  me: Me;
  domain: Domain | null;
  apiKey: ApiKey | null;
  setApiKey: (k: ApiKey) => void;
  onDone: () => Promise<void>;
  onBack: () => void;
}) {
  const [name, setName] = useState('Первый ключ');
  const [busy, setBusy] = useState(false);
  const from = domain?.status === 'verified' ? `hello@${domain.name}` : `hello@${domain?.name ?? t('ваш-домен.com')}`;

  async function create() {
    setBusy(true);
    try {
      setApiKey(await api.post<ApiKey>('/api-keys', { name, mode: 'test' }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h1>{t('Риф: ключ для вашего кода')}</h1>
      <p className="lead">{t('API-ключ даёт серверу право отправлять письма. Мы храним только его хеш — показать ключ повторно не сможем.')}</p>
      <div className="stack" style={{ marginTop: 24 }}>
        {done && !apiKey?.key && <div className="callout ok"><IconCheck /> {t('Ключ уже создан. Новые ключи — в разделе «API-ключи» бэкофиса.')}</div>}
        {!apiKey?.key ? (
          <Field label={t('Название ключа')} hint={t('Например: «Backend production» или «Staging»')}>
            <div className="row">
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
              <button className="btn btn-primary" onClick={create} disabled={busy || !name}>{t('Создать ключ')}</button>
            </div>
          </Field>
        ) : (
          <>
            <div className="secret-box">
              <code>{apiKey.key}</code>
              <CopyButton value={apiKey.key} />
            </div>
            <div className="callout warn">{t('Сохраните ключ в менеджере секретов или переменной окружения MAILOCEAN_API_KEY. После закрытия шага он скрывается навсегда.')}</div>
            <CodeTabs snippets={sendSnippets(apiKey.key, from, me.user.email)} />
          </>
        )}
      </div>
      <div className="wizard-actions">
        <button className="btn btn-ghost" onClick={onBack}><IconArrowLeft size={16} /> {t('Назад')}</button>
        <span className="spacer" />
        <button className="btn btn-primary btn-lg" disabled={!apiKey?.key && !done} onClick={onDone}>
          {apiKey?.key ? t('Я сохранил ключ') : t('Дальше')} <IconArrowRight size={16} />
        </button>
      </div>
    </>
  );
}

function TestEmailStep({ done, me, domain, onDone, onSkip, onBack }: { done: boolean; me: Me; domain: Domain | null; onDone: () => Promise<void>; onSkip: () => void; onBack: () => void }) {
  const [email, setEmail] = useState<EmailDetail | null>(null);
  const [flying, setFlying] = useState(false);
  const [error, setError] = useState('');
  const verified = domain?.status === 'verified' ? domain : null;

  async function send() {
    setError('');
    setFlying(true);
    try {
      const res = await api.post<{ id: string }>('/test-email', verified ? { domain_id: verified.id } : {});
      for (let i = 0; i < 40; i++) {
        const e = await api.get<EmailDetail>(`/emails/${res.id}`);
        setEmail(e);
        if (e.status !== 'queued') break;
        await new Promise((r) => setTimeout(r, 500));
      }
    } catch (e) {
      setError((e as Error).message);
      setFlying(false);
    }
  }

  const ok = email && ['sent', 'delivered'].includes(email.status);
  return (
    <>
      <h1>{t('Глубина: первое письмо')}</h1>
      <p className="lead">
        {t('Отправим тестовое письмо на')} <b>{me.user.email}</b> {t('с адреса')} <b>{verified ? `hello@${verified.name}` : `onboarding@${me.platform.sender_domain}`}</b> {t('и покажем его путь по шагам.')}
      </p>
      <div className="grid" style={{ gridTemplateColumns: '1fr 1.2fr', marginTop: 24, alignItems: 'start' }}>
        <div className="stack" style={{ alignItems: 'center' }}>
          <LetterLaunch flying={flying} />
          {!email && (
            <button className="btn btn-primary btn-lg" onClick={send} disabled={flying} data-track="test_email.send_click">
              <IconSend size={16} /> {flying ? t('Отправляем…') : t('Отправить себе письмо')}
            </button>
          )}
          {me.platform.delivery_provider === 'sandbox' && (
            <p className="small muted" style={{ textAlign: 'center' }}>{t('Режим песочницы: письмо проходит весь путь внутри MailOcean, но наружу не уходит.')}</p>
          )}
        </div>
        <div className="stack">
          {email ? (
            <>
              <div className="row">
                <StatusPill status={email.status} />
                <code className="small muted">{email.id}</code>
              </div>
              <Dive events={email.timeline} />
              {ok && <div className="callout ok"><IconCheck /> {t('Первая волна дошла до провайдера за {ms} мс.', { ms: email.timeline.at(-1)?.elapsed_ms ?? '' })}</div>}
            </>
          ) : (
            <div className="callout">{t('Здесь появится путь письма: приём API → очередь → передача провайдеру → ответ провайдера.')}</div>
          )}
          {error && <div className="callout bad">{error}</div>}
        </div>
      </div>
      <div className="wizard-actions">
        <button className="btn btn-ghost" onClick={onBack}><IconArrowLeft size={16} /> {t('Назад')}</button>
        <span className="spacer" />
        {!ok && <button className="btn btn-ghost" onClick={onSkip}>{t('Пропустить')}</button>}
        <button className="btn btn-primary btn-lg" disabled={!ok && !done} onClick={onDone}>
          {t('Глубже')} <IconArrowRight size={16} />
        </button>
      </div>
    </>
  );
}

function WebhookStep({ done, onDone, onSkip, onBack }: { done: boolean; onDone: () => Promise<void>; onSkip: () => void; onBack: () => void }) {
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<string[]>(['email.delivered', 'email.bounced', 'email.complained', 'email.failed']);
  const [hook, setHook] = useState<Webhook | null>(null);
  const [error, setError] = useState('');
  const toggle = (e: string) => setEvents((xs) => (xs.includes(e) ? xs.filter((x) => x !== e) : [...xs, e]));

  async function create() {
    setError('');
    try {
      setHook(await api.post<Webhook>('/webhooks', { url, events }));
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <>
      <h1>{t('Бездна: события в ваш сервер')}</h1>
      <p className="lead">{t('MailOcean сообщит вашему бэкенду о доставке, отскоках и жалобах. Каждый запрос подписан HMAC-SHA256.')}</p>
      <div className="stack" style={{ marginTop: 24 }}>
        {!hook ? (
          <>
            <Field label={t('URL эндпоинта')} hint={t('Только https и публичный адрес')}>
              <input className="input input-mono" placeholder="https://api.coffee-north.com/webhooks/mailocean" value={url} onChange={(e) => setUrl(e.target.value)} />
            </Field>
            <Field label={t('События')}>
              <div className="chips">
                {WEBHOOK_EVENTS.map((e) => (
                  <button key={e} type="button" className={`chip${events.includes(e) ? ' on' : ''}`} onClick={() => toggle(e)}>{e}</button>
                ))}
              </div>
            </Field>
            {error && <div className="callout bad">{error}</div>}
            <div><button className="btn btn-primary" onClick={create} disabled={!url || !events.length}>{t('Создать вебхук')}</button></div>
          </>
        ) : (
          <>
            <div className="callout ok"><IconCheck /> {t('Вебхук создан: {url}', { url: hook.url })}</div>
            <Field label={t('Секрет подписи (показывается один раз)')}>
              <div className="secret-box">
                <code>{hook.secret}</code>
                <CopyButton value={hook.secret ?? ''} />
              </div>
            </Field>
            <pre className="code">{`${t('// Проверка подписи (Node.js)')}
const [t, v1] = req.headers['mailocean-signature'].split(',').map(p => p.split('=')[1]);
const expected = crypto.createHmac('sha256', process.env.MAILOCEAN_WEBHOOK_SECRET)
  .update(\`\${t}.\${rawBody}\`).digest('hex');
if (!crypto.timingSafeEqual(Buffer.from(v1), Buffer.from(expected))) return res.sendStatus(401);`}</pre>
          </>
        )}
      </div>
      <div className="wizard-actions">
        <button className="btn btn-ghost" onClick={onBack}><IconArrowLeft size={16} /> {t('Назад')}</button>
        <span className="spacer" />
        {!hook && <button className="btn btn-ghost" onClick={onSkip}>{t('Пропустить')}</button>}
        <button className="btn btn-primary btn-lg" disabled={!hook && !done} onClick={onDone}>
          {t('На дно')} <IconArrowRight size={16} />
        </button>
      </div>
    </>
  );
}

function FinishStep({ me, domain }: { me: Me; domain: Domain | null }) {
  const { setMe } = useSession();
  const nav = useNavigate();
  const done = me.workspace.onboarding.completed;
  const items: [OnboardingStep, string][] = [
    ['profile', t('Профиль компании')],
    ['domain', domain ? t('Домен {name}', { name: domain.name }) : t('Домен отправки')],
    ['api_key', t('API-ключ')],
    ['test_email', t('Первое письмо')],
    ['webhook', t('Вебхуки')],
  ];
  async function finish() {
    setMe(await api.post<Me>('/onboarding/finish'));
    nav('/');
  }
  return (
    <div className="stack" style={{ alignItems: 'center', textAlign: 'center', padding: '20px 0' }}>
      <div style={{ fontSize: 64, animation: 'bob 3s ease-in-out infinite' }}>🐋</div>
      <h1>{t('Вы в океане')}</h1>
      <p className="lead" style={{ margin: '0 auto' }}>{t('11 000 метров — глубже только ваши метрики. Аккаунт готов принимать трафик.')}</p>
      <div className="stack" style={{ margin: '20px 0', textAlign: 'left', minWidth: 300 }}>
        {items.map(([id, label]) => (
          <div key={id} className="row">
            <span className={`pill ${done.includes(id) ? 'ok' : 'pending'}`}>{done.includes(id) ? t('готово') : t('позже')}</span>
            {label}
          </div>
        ))}
      </div>
      <button className="btn btn-primary btn-lg" onClick={finish}>
        {t('Открыть бэкофис')} <IconArrowRight size={16} />
      </button>
    </div>
  );
}
